import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, ArrowLeft, RefreshCw, Eye, Trash2, CheckCircle, XCircle,
  Search, Filter, Tag, DollarSign, Users, ShoppingBag, TrendingUp,
  Plus, Edit, Save, X
} from 'lucide-react';
import api, { getErrorMessage } from '../../utils/api';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/imageUrl';

const P2PAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [listings, setListings] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Modal states
  const [showTierModal, setShowTierModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Form states
  const [tierForm, setTierForm] = useState({
    name: '', price: 100, value_limit: 5000, max_images: 3, 
    allow_video: false, allow_pdf: false, priority_listing: false, is_active: true
  });
  const [categoryForm, setCategoryForm] = useState({ name: '', slug: '', icon: '', parent_id: '', is_active: true });

  useEffect(() => {
    fetchData();
  }, [page, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, listingsRes, tiersRes, catsRes] = await Promise.all([
        api.get('/admin/p2p/stats'),
        api.get(`/admin/p2p/listings?page=${page}&limit=20${statusFilter ? `&status=${statusFilter}` : ''}`),
        api.get('/admin/p2p/tiers'),
        api.get('/admin/p2p/categories')
      ]);
      setStats(statsRes.data);
      setListings(listingsRes.data.listings || []);
      setTotalPages(listingsRes.data.pages || 1);
      setTiers(tiersRes.data || []);
      setCategories(catsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch P2P data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // TIER CRUD
  const saveTier = async () => {
    try {
      if (editingTier) {
        await api.put(`/admin/p2p/tier/${editingTier.id}`, tierForm);
        toast.success('Tier updated!');
      } else {
        await api.post('/admin/p2p/tier', tierForm);
        toast.success('Tier created!');
      }
      setShowTierModal(false);
      setEditingTier(null);
      resetTierForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save tier'));
    }
  };

  const deleteTier = async (tierId) => {
    if (!window.confirm('Delete this tier?')) return;
    try {
      await api.delete(`/admin/p2p/tier/${tierId}`);
      toast.success('Tier deleted!');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete tier'));
    }
  };

  const editTier = (tier) => {
    setEditingTier(tier);
    setTierForm({
      name: tier.name, price: tier.price, value_limit: tier.value_limit,
      max_images: tier.max_images, allow_video: tier.allow_video,
      allow_pdf: tier.allow_pdf, priority_listing: tier.priority_listing, is_active: tier.is_active
    });
    setShowTierModal(true);
  };

  const resetTierForm = () => {
    setTierForm({ name: '', price: 100, value_limit: 5000, max_images: 3, 
      allow_video: false, allow_pdf: false, priority_listing: false, is_active: true });
  };

  // CATEGORY CRUD
  const saveCategory = async () => {
    try {
      if (editingCategory) {
        await api.put(`/admin/p2p/category/${editingCategory.id}`, categoryForm);
        toast.success('Category updated!');
      } else {
        await api.post('/admin/p2p/category', categoryForm);
        toast.success('Category created!');
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm({ name: '', slug: '', icon: '', parent_id: '', is_active: true });
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save category'));
    }
  };

  const deleteCategory = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/admin/p2p/category/${catId}`);
      toast.success('Category deleted!');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete category'));
    }
  };

  const editCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({ name: cat.name, slug: cat.slug, icon: cat.icon || '', parent_id: cat.parent_id || '', is_active: cat.is_active });
    setShowCategoryModal(true);
  };

  // LISTING ACTIONS
  const updateListingStatus = async (listingId, status) => {
    try {
      await api.put(`/admin/p2p/listing/${listingId}`, { status });
      toast.success('Listing updated');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update listing'));
    }
  };

  const toggleFeature = async (listingId, currentFeatured) => {
    try {
      await api.put(`/admin/p2p/listing/${listingId}`, { is_featured: !currentFeatured });
      toast.success(currentFeatured ? 'Unfeatured' : 'Featured');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update'));
    }
  };

  const deleteListing = async (listingId) => {
    if (!window.confirm('Delete this listing permanently?')) return;
    try {
      await api.delete(`/admin/p2p/listing/${listingId}`);
      toast.success('Listing deleted!');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete listing'));
    }
  };

  if (loading && !stats) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="p2p-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" className="text-muted-foreground hover:text-white text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Admin
            </Link>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Package className="w-8 h-8 text-primary" />
              P2P Marketplace Admin
            </h1>
          </div>
          <button onClick={fetchData} className="p-3 rounded-xl bg-white/10 hover:bg-white/20">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="glass-card p-5">
            <ShoppingBag className="w-7 h-7 text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{stats?.total_listings || 0}</p>
            <p className="text-sm text-muted-foreground">Total Listings</p>
          </div>
          <div className="glass-card p-5">
            <CheckCircle className="w-7 h-7 text-green-500 mb-2" />
            <p className="text-2xl font-bold">{stats?.active_listings || 0}</p>
            <p className="text-sm text-muted-foreground">Active Listings</p>
          </div>
          <div className="glass-card p-5">
            <Tag className="w-7 h-7 text-yellow-500 mb-2" />
            <p className="text-2xl font-bold">{stats?.sell_listings || 0} / {stats?.buy_listings || 0}</p>
            <p className="text-sm text-muted-foreground">Sell / Buy</p>
          </div>
          <div className="glass-card p-5">
            <DollarSign className="w-7 h-7 text-emerald-500 mb-2" />
            <p className="text-2xl font-bold">₹{(stats?.total_revenue || 0).toLocaleString()}</p>
            <p className="text-sm text-muted-foreground">Pass Revenue</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {['overview', 'listings', 'tiers', 'categories'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap ${
                activeTab === tab ? 'bg-primary text-white' : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">All Listings</h2>
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="px-4 py-2 bg-white/10 border border-white/10 rounded-lg"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="sold">Sold</option>
                <option value="expired">Expired</option>
                <option value="removed">Removed</option>
              </select>
            </div>

            <div className="space-y-4">
              {listings.map((listing) => (
                <div key={listing.id} className="p-4 bg-white/5 rounded-xl flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {listing.images?.[0] && (
                      <img src={getImageUrl(listing.images[0])} alt="" className="w-16 h-16 rounded-lg object-cover" />
                    )}
                    <div>
                      <h3 className="font-medium">{listing.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        ₹{listing.price?.toLocaleString()} • {listing.listing_type} • {listing.pass_tier}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Views: {listing.views} • Status: <span className={listing.status === 'active' ? 'text-green-500' : 'text-yellow-500'}>{listing.status}</span>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleFeature(listing.id, listing.is_featured)}
                      className={`p-2 rounded-lg ${listing.is_featured ? 'bg-yellow-500/20 text-yellow-500' : 'bg-white/10'}`}
                      title={listing.is_featured ? 'Unfeature' : 'Feature'}
                    >
                      ⭐
                    </button>
                    {listing.status === 'active' && (
                      <button
                        onClick={() => updateListingStatus(listing.id, 'removed')}
                        className="p-2 rounded-lg bg-orange-500/20 text-orange-500"
                        title="Remove"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => deleteListing(listing.id)}
                      className="p-2 rounded-lg bg-red-500/20 text-red-500"
                      title="Delete Permanently"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {listings.length === 0 && (
                <p className="text-center text-muted-foreground py-8">No listings found</p>
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 bg-white/10 rounded-lg disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-4 py-2">Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-white/10 rounded-lg disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Tiers Tab */}
        {activeTab === 'tiers' && (
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Pass Tiers</h2>
              <button
                onClick={() => { resetTierForm(); setEditingTier(null); setShowTierModal(true); }}
                className="px-4 py-2 bg-primary text-white rounded-lg flex items-center gap-2 hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" /> Add Tier
              </button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tiers.map((tier) => (
                <div key={tier.id} className={`p-4 rounded-xl border ${tier.is_active ? 'border-green-500/30 bg-green-500/5' : 'border-white/10 bg-white/5'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-lg">{tier.name}</h3>
                    <div className="flex gap-2">
                      <button onClick={() => editTier(tier)} className="p-1.5 bg-blue-500/20 text-blue-500 rounded hover:bg-blue-500/30">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => deleteTier(tier.id)} className="p-1.5 bg-red-500/20 text-red-500 rounded hover:bg-red-500/30">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p className="text-2xl font-bold text-primary mb-2">₹{tier.price?.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground mb-2">Value Limit: ₹{tier.value_limit?.toLocaleString()}</p>
                  <div className="text-xs space-y-1">
                    <p>Max Images: {tier.max_images}</p>
                    <p>Video: {tier.allow_video ? '✅' : '❌'} | PDF: {tier.allow_pdf ? '✅' : '❌'}</p>
                    <p>Priority: {tier.priority_listing ? '✅' : '❌'}</p>
                  </div>
                  <span className={`mt-2 inline-block text-xs px-2 py-1 rounded ${tier.is_active ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                    {tier.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Categories ({categories.length})</h2>
              <button
                onClick={() => { setCategoryForm({ name: '', slug: '', icon: '', parent_id: '', is_active: true }); setEditingCategory(null); setShowCategoryModal(true); }}
                className="px-4 py-2 bg-primary text-white rounded-lg flex items-center gap-2 hover:bg-primary/90"
              >
                <Plus className="w-4 h-4" /> Add Category
              </button>
            </div>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
              {/* Main Categories */}
              {categories.filter(cat => !cat.parent_id).map((cat) => (
                <div key={cat.id} className="space-y-2">
                  <div className={`p-3 rounded-lg flex items-center justify-between ${cat.is_active ? 'bg-primary/20 border border-primary/30' : 'bg-white/5 opacity-50'}`}>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        {cat.icon && <span>{cat.icon}</span>}
                        {cat.name}
                      </p>
                      <p className="text-xs text-muted-foreground">/{cat.slug}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => editCategory(cat)} className="p-1.5 bg-blue-500/20 text-blue-500 rounded hover:bg-blue-500/30">
                        <Edit className="w-3 h-3" />
                      </button>
                      <button onClick={() => deleteCategory(cat.id)} className="p-1.5 bg-red-500/20 text-red-500 rounded hover:bg-red-500/30">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  {/* Sub-categories */}
                  {categories.filter(sub => sub.parent_id === cat.id).map(sub => (
                    <div key={sub.id} className={`ml-4 p-2 rounded-lg flex items-center justify-between text-sm ${sub.is_active ? 'bg-white/10' : 'bg-white/5 opacity-50'}`}>
                      <div>
                        <p className="font-medium flex items-center gap-1">
                          <span className="text-muted-foreground">└</span>
                          {sub.icon && <span>{sub.icon}</span>}
                          {sub.name}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => editCategory(sub)} className="p-1 bg-blue-500/20 text-blue-500 rounded hover:bg-blue-500/30">
                          <Edit className="w-2.5 h-2.5" />
                        </button>
                        <button onClick={() => deleteCategory(sub.id)} className="p-1 bg-red-500/20 text-red-500 rounded hover:bg-red-500/30">
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="glass-card p-6">
              <h2 className="text-xl font-bold mb-4">Pass Sales Summary</h2>
              {stats?.passes_sold && Object.keys(stats.passes_sold).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(stats.passes_sold).map(([tier, count]) => (
                    <div key={tier} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <span className="font-medium">{tier}</span>
                      <span className="text-primary font-bold">{count} sold</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground">No passes sold yet</p>
              )}
            </div>

            <div className="glass-card p-6">
              <h2 className="text-xl font-bold mb-4">Quick Stats</h2>
              <div className="space-y-3">
                <div className="flex justify-between p-3 bg-white/5 rounded-lg">
                  <span>Total Categories</span>
                  <span className="font-bold">{categories.length}</span>
                </div>
                <div className="flex justify-between p-3 bg-white/5 rounded-lg">
                  <span>Active Tiers</span>
                  <span className="font-bold">{tiers.filter(t => t.is_active).length}</span>
                </div>
                <div className="flex justify-between p-3 bg-white/5 rounded-lg">
                  <span>Avg Listing Price</span>
                  <span className="font-bold">-</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tier Modal */}
      {showTierModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">{editingTier ? 'Edit Tier' : 'Create Tier'}</h2>
            <div className="space-y-4">
              <input type="text" placeholder="Tier Name (e.g., GOLD)" value={tierForm.name}
                onChange={(e) => setTierForm({ ...tierForm, name: e.target.value.toUpperCase() })}
                className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" />
              <div className="grid grid-cols-2 gap-4">
                <input type="number" placeholder="Price (₹)" value={tierForm.price}
                  onChange={(e) => setTierForm({ ...tierForm, price: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" />
                <input type="number" placeholder="Value Limit (₹)" value={tierForm.value_limit}
                  onChange={(e) => setTierForm({ ...tierForm, value_limit: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" />
              </div>
              <input type="number" placeholder="Max Images" value={tierForm.max_images}
                onChange={(e) => setTierForm({ ...tierForm, max_images: parseInt(e.target.value) })}
                className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" />
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={tierForm.allow_video}
                    onChange={(e) => setTierForm({ ...tierForm, allow_video: e.target.checked })} />
                  Allow Video
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={tierForm.allow_pdf}
                    onChange={(e) => setTierForm({ ...tierForm, allow_pdf: e.target.checked })} />
                  Allow PDF
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={tierForm.priority_listing}
                    onChange={(e) => setTierForm({ ...tierForm, priority_listing: e.target.checked })} />
                  Priority Listing
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={tierForm.is_active}
                    onChange={(e) => setTierForm({ ...tierForm, is_active: e.target.checked })} />
                  Active
                </label>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowTierModal(false); setEditingTier(null); }}
                  className="flex-1 py-3 bg-white/10 rounded-xl font-semibold">Cancel</button>
                <button onClick={saveTier} className="flex-1 py-3 bg-primary text-white rounded-xl font-semibold">
                  {editingTier ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">{editingCategory ? 'Edit Category' : 'Create Category / Sub-Category'}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Category Name *</label>
                <input type="text" placeholder="Category Name" value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Slug (auto-generated)</label>
                <input type="text" placeholder="Slug" value={categoryForm.slug}
                  onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Icon (emoji or icon name)</label>
                <input type="text" placeholder="📦 or ShoppingBag" value={categoryForm.icon}
                  onChange={(e) => setCategoryForm({ ...categoryForm, icon: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Parent Category (for sub-category)</label>
                <select
                  value={categoryForm.parent_id}
                  onChange={(e) => setCategoryForm({ ...categoryForm, parent_id: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                >
                  <option value="">-- None (Main Category) --</option>
                  {categories.filter(c => !c.parent_id).map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Leave empty for main category, select parent for sub-category
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={categoryForm.is_active}
                  onChange={(e) => setCategoryForm({ ...categoryForm, is_active: e.target.checked })} />
                Active
              </label>
              <div className="flex gap-3">
                <button onClick={() => { setShowCategoryModal(false); setEditingCategory(null); }}
                  className="flex-1 py-3 bg-white/10 rounded-xl font-semibold">Cancel</button>
                <button onClick={saveCategory} className="flex-1 py-3 bg-primary text-white rounded-xl font-semibold">
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default P2PAdmin;
